import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as admin from '@shared/api/admin';
import * as feedback from '@shared/api/feedback';
import * as authApi from '@shared/api/auth';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { useMe } from './use-me';
import type { Uuid } from '@shared/types/api';

const authed = () => !!getTokenStore().getAccessToken();

// ============================================================
// KYC (re-exported from the original use-admin-kyc — kept there
// so the existing KYC page doesn't need to change).
// ============================================================

export {
  useAdminKycList,
  useAdminKycOne,
  useApproveKyc,
  useRejectKyc,
} from './use-admin-kyc';

// ============================================================
// Users
// ============================================================

export function useAdminUsers(params: admin.ListAdminUsersParams = {}) {
  return useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: () => admin.listAdminUsers(params),
    enabled: authed(),
    staleTime: 15_000,
  });
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: Uuid; reason?: string }) =>
      admin.suspendAdminUser(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('User account suspended successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to suspend user');
    },
  });
}

export function useReinstateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.reinstateAdminUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('User account reinstated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to reinstate user');
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.deleteAdminUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('User account deleted successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to delete user');
    },
  });
}

export function useSetAdminRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      adminRole,
    }: {
      id: Uuid;
      adminRole: admin.AdminKycListItem extends unknown ? 'SUPER_ADMIN' | 'OPS_ADMIN' | 'READ_ONLY_ADMIN' | null : never;
    }) => admin.setAdminRole(id, adminRole),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Admin role updated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to update admin role');
    },
  });
}

export function useAdminUserFeedbacks(id: Uuid | null) {
  return useQuery({
    queryKey: id ? ['admin', 'users', 'feedbacks', id] : ['admin', 'users', 'feedbacks', 'disabled'],
    queryFn: () => admin.getAdminUserFeedbacks(id as Uuid),
    enabled: authed() && !!id,
    staleTime: 30_000,
  });
}

// ============================================================
// Transactions
// ============================================================

export function useAdminTransactions(params: admin.ListAdminTxParams = {}) {
  return useQuery({
    queryKey: ['admin', 'tx', params],
    queryFn: () => admin.listAdminTransactions(params),
    enabled: authed(),
    staleTime: 10_000,
  });
}

export function useAdminTransaction(id: Uuid | undefined) {
  return useQuery({
    queryKey: id ? ['admin', 'tx', 'one', id] : ['admin', 'tx', 'one', 'disabled'],
    queryFn: () => admin.getAdminTransaction(id as Uuid),
    enabled: authed() && !!id,
    staleTime: 10_000,
  });
}

export function useAdminTxHistory(id: Uuid | undefined) {
  return useQuery({
    queryKey: id ? ['admin', 'tx', 'history', id] : ['admin', 'tx', 'history', 'disabled'],
    queryFn: () => admin.getAdminTransactionHistory(id as Uuid),
    enabled: authed() && !!id,
    staleTime: 10_000,
  });
}

function invalidateTx(qc: ReturnType<typeof useQueryClient>, id?: Uuid) {
  qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
  if (id) qc.invalidateQueries({ queryKey: ['admin', 'tx', 'one', id] });
}

export function useAssignAssociate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      associateUserId,
      associateUserIds,
      reason,
    }: {
      id: Uuid;
      associateUserId?: Uuid;
      associateUserIds?: Uuid[];
      reason?: string;
    }) => admin.assignAssociate(id, { associateUserId, associateUserIds, reason }),
    onSuccess: (_, v) => invalidateTx(qc, v.id),
  });
}

export function useRevokeAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: Uuid; reason: string }) =>
      admin.revokeAssignment(id, reason),
    onSuccess: (_, v) => invalidateTx(qc, v.id),
  });
}

export function useForceComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.forceCompleteTransaction(id),
    onSuccess: (_, id) => invalidateTx(qc, id),
  });
}

export function useForceCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: Uuid; reason: string }) =>
      admin.forceCancelTransaction(id, reason),
    onSuccess: (_, v) => invalidateTx(qc, v.id),
  });
}

export function useReviewCancellationRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      reason,
    }: {
      id: Uuid;
      action: 'ACCEPT' | 'REJECT';
      reason?: string;
    }) => admin.reviewCancellationRequest(id, { action, reason }),
    onSuccess: (_, v) => {
      invalidateTx(qc, v.id);
      qc.invalidateQueries({ queryKey: ['chat', v.id, 'history'] });
    },
  });
}

// ============================================================
// Invoices
// ============================================================

export function useAdminInvoices(params: admin.ListInvoicesParams = {}) {
  return useQuery({
    queryKey: ['admin', 'invoices', params],
    queryFn: () => admin.listInvoices(params),
    enabled: authed(),
    staleTime: 10_000,
  });
}

export function useAdminInvoice(id: Uuid | undefined) {
  return useQuery({
    queryKey: ['admin', 'invoice', id],
    queryFn: () => admin.getInvoice(id!),
    enabled: authed() && Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: admin.CreateInvoicePayload) => admin.createInvoice(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invoices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
    },
  });
}

export function useMarkInvoiceSent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.markInvoiceSent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invoices'] }),
  });
}

export function useCancelInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: Uuid; reason: string }) =>
      admin.cancelInvoice(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invoices'] }),
  });
}

export function useMarkInvoicePaidOffline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      reference,
      notes,
    }: {
      id: Uuid;
      reference: string;
      notes?: string;
    }) => admin.markInvoicePaidOffline(id, { reference, notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invoices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
    },
  });
}

// ============================================================
// Refunds
// ============================================================

export function useAdminRefunds(params: admin.ListRefundsParams = {}) {
  return useQuery({
    queryKey: ['admin', 'refunds', params],
    queryFn: () => admin.listAdminRefunds(params),
    enabled: authed(),
    staleTime: 10_000,
  });
}

export function useApproveRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      finalRefundPaise,
      notes,
    }: {
      id: Uuid;
      finalRefundPaise?: string;
      notes?: string;
    }) => admin.approveRefund(id, { finalRefundPaise, notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'refunds'] }),
  });
}

export function useRejectRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: Uuid; reason: string }) =>
      admin.rejectRefund(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'refunds'] }),
  });
}

export function useMarkRefundedOffline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      bankReference,
      finalRefundPaise,
      notes,
    }: {
      id: Uuid;
      bankReference: string;
      finalRefundPaise?: string;
      notes?: string;
    }) => admin.markRefundedOffline(id, { bankReference, finalRefundPaise, notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'refunds'] }),
  });
}

// ============================================================
// Payouts
// ============================================================

export function useAdminPayouts(params: admin.ListPayoutsParams = {}) {
  return useQuery({
    queryKey: ['admin', 'payouts', params],
    queryFn: () => admin.listAdminPayouts(params),
    enabled: authed(),
    staleTime: 10_000,
  });
}

export function useEligiblePayoutTransactions() {
  return useQuery({
    queryKey: ['admin', 'payouts', 'eligible-transactions'],
    queryFn: () => admin.getEligiblePayoutTransactions(),
    enabled: authed(),
    staleTime: 10_000,
  });
}

export function useCreatePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (transactionId: Uuid) => admin.createAdminPayout(transactionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'payouts', 'eligible-transactions'] });
    },
  });
}

export function useMarkPayoutPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      transactionReference,
      notes,
    }: {
      id: Uuid;
      transactionReference: string;
      notes?: string;
    }) => admin.markPayoutPaid(id, { transactionReference, notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payouts'] }),
  });
}

export function useHoldPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: Uuid; notes: string }) => admin.holdPayout(id, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payouts'] }),
  });
}

export function useCancelPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: Uuid; notes: string }) =>
      admin.cancelPayout(id, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payouts'] }),
  });
}

// ============================================================
// Services (catalog admin)
// ============================================================

import { listAllServices, listServices, type ListServicesParams } from '@shared/api/services';

export function useAdminServices(includeInactive = true) {
  return useQuery({
    queryKey: ['admin', 'services', includeInactive],
    queryFn: () => listAllServices({ includeInactive }),
    enabled: authed(),
    staleTime: 30_000,
  });
}

export function useAdminServicesList(params: ListServicesParams = {}) {
  return useQuery({
    queryKey: ['admin', 'services', 'list', params],
    queryFn: () => listServices(params),
    enabled: authed(),
    staleTime: 10_000,
  });
}

export function useCreateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: admin.adminCreateService,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'services'] });
      toast.success('Catalog service created successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to create service');
    },
  });
}

export function useUpdateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: Uuid; payload: Parameters<typeof admin.adminUpdateService>[1] }) =>
      admin.adminUpdateService(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'services'] });
      toast.success('Catalog service updated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to update service');
    },
  });
}

export function useActivateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.adminActivateService(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'services'] });
      toast.success('Service activated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to activate service');
    },
  });
}

export function useDeactivateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.adminDeactivateService(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'services'] });
      toast.success('Service deactivated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to deactivate service');
    },
  });
}

export function useReorderServices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { ids: Uuid[]; startIndex?: number }) => admin.adminReorderServices(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'services'] }),
  });
}

// ============================================================
// Config
// ============================================================

export function useAdminConfig() {
  return useQuery({
    queryKey: ['admin', 'config'],
    queryFn: admin.getPlatformConfig,
    enabled: authed(),
    staleTime: 60_000,
  });
}

export function useUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: admin.updatePlatformConfig,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'config'] }),
  });
}

// ============================================================
// Reports
// ============================================================

export function useRevenueReport(from: string, to: string, bucket: 'day' | 'month' = 'month') {
  return useQuery({
    queryKey: ['admin', 'reports', 'revenue', { from, to, bucket }],
    queryFn: () => admin.revenueReport({ fromDate: from, toDate: to, bucket }),
    enabled: authed() && !!from && !!to,
    staleTime: 60_000,
  });
}

export function useGstReport(from: string, to: string) {
  return useQuery({
    queryKey: ['admin', 'reports', 'gst', { from, to }],
    queryFn: () => admin.gstReport({ fromDate: from, toDate: to }),
    enabled: authed() && !!from && !!to,
    staleTime: 60_000,
  });
}

export function useTdsReport(from: string, to: string) {
  return useQuery({
    queryKey: ['admin', 'reports', 'tds', { from, to }],
    queryFn: () => admin.tdsReport({ fromDate: from, toDate: to }),
    enabled: authed() && !!from && !!to,
    staleTime: 60_000,
  });
}

// ============================================================
// Feedbacks
// ============================================================

export function useAdminFeedbacks(params: feedback.ListFeedbacksParams = {}) {
  return useQuery({
    queryKey: ['admin', 'feedbacks', params],
    queryFn: () => feedback.listFeedbacks(params),
    enabled: authed(),
    staleTime: 15_000,
  });
}

// ============================================================
// Authorities
// ============================================================

export function useAdminAuthorities() {
  return useQuery({
    queryKey: ['admin', 'authorities'],
    queryFn: () => admin.listAuthorities(),
    enabled: authed(),
    staleTime: 15_000,
  });
}

export function useAdminAuthority(id: Uuid | undefined) {
  return useQuery({
    queryKey: id ? ['admin', 'authorities', id] : ['admin', 'authorities', 'disabled'],
    queryFn: () => admin.getAuthority(id as Uuid),
    enabled: authed() && !!id,
    staleTime: 15_000,
  });
}

export function useUploadServiceImage() {
  return useMutation({
    mutationFn: (file: File) => admin.uploadServiceImage(file),
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to upload image');
    },
  });
}

export function useCreateAuthority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: admin.CreateAuthorityPayload) => admin.createAuthority(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Authority saved successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to save authority');
    },
  });
}

export function useUpdateAuthority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: Uuid; payload: admin.UpdateAuthorityPayload }) =>
      admin.updateAuthority(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Authority updated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to update authority');
    },
  });
}

export function useArchiveAuthority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.archiveAuthority(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Authority archived successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to archive authority');
    },
  });
}

export function useUnarchiveAuthority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.unarchiveAuthority(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Authority unarchived successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to unarchive authority');
    },
  });
}

export function useDeleteAuthority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => admin.deleteAuthority(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Authority deleted successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to delete authority');
    },
  });
}

export function useCreateAuthorityService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: admin.CreateAuthorityServicePayload) => admin.createAuthorityService(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Service created successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to create service');
    },
  });
}

export function useUpdateAuthorityService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      serviceId,
      payload,
    }: {
      serviceId: Uuid;
      payload: admin.UpdateAuthorityServicePayload;
    }) => admin.updateAuthorityService(serviceId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Service updated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to update service');
    },
  });
}

export function useArchiveAuthorityService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (serviceId: Uuid) => admin.archiveAuthorityService(serviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Service archived successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to archive service');
    },
  });
}

export function useUnarchiveAuthorityService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (serviceId: Uuid) => admin.unarchiveAuthorityService(serviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Service unarchived successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to unarchive service');
    },
  });
}

export function useDeleteAuthorityService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (serviceId: Uuid) => admin.deleteAuthorityService(serviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success('Service deleted successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to delete service');
    },
  });
}

export function useBulkImportAuthorities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: admin.BulkImportAuthoritiesPayload) => admin.bulkImportAuthorities(payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'authorities'] });
      toast.success(`Import completed: ${res.createdAuthoritiesCount} authorities & ${res.createdServicesCount} services added`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to import excel file');
    },
  });
}

// ============================================================
// Admin Verification & Approvals (Super Admin)
// ============================================================

export function useAdminVerificationUsers(
  status?: string,
  options?: { enabled?: boolean },
) {
  const { data: me } = useMe();
  const session = useAuthStore((s) => s.session);
  const currentAdminRole = me?.adminRole ?? session?.adminRole;
  const isSuperAdmin = currentAdminRole === 'SUPER_ADMIN';

  const isEnabled = authed() && isSuperAdmin && (options?.enabled ?? true);

  return useQuery({
    queryKey: ['admin', 'verification', 'users', status ?? 'ALL'],
    queryFn: () => authApi.fetchAdminVerificationUsers(status),
    enabled: isEnabled,
    staleTime: 10_000,
  });
}

export function useAcceptAdminVerificationUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => authApi.acceptAdminVerificationUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'verification'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Admin account accepted and activated');
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to approve admin account',
      );
    },
  });
}

export function useRejectAdminVerificationUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: Uuid; reason: string }) =>
      authApi.rejectAdminVerificationUser(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'verification'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Admin application declined with reason');
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to decline admin account',
      );
    },
  });
}

export function useResubmitAdminProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: authApi.AdminResubmitProfilePayload) =>
      authApi.adminResubmitProfile(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      toast.success('Profile updated and resubmitted for verification');
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to resubmit profile',
      );
    },
  });
}

// ============================================================
// Audit Logs
// ============================================================

export function useAdminAuditLogs(params: admin.ListAdminAuditLogsParams = {}) {
  return useQuery({
    queryKey: ['admin', 'audit-logs', params],
    queryFn: () => admin.listAdminAuditLogs(params),
    enabled: authed(),
    staleTime: 10_000,
  });
}

export function useAdminAuditLog(id: Uuid | null) {
  return useQuery({
    queryKey: ['admin', 'audit-logs', id],
    queryFn: () => admin.getAdminAuditLog(id!),
    enabled: authed() && Boolean(id),
    staleTime: 60_000,
  });
}

export function useAdminAuditLogMeta() {
  return useQuery({
    queryKey: ['admin', 'audit-logs-meta'],
    queryFn: () => admin.getAdminAuditLogMeta(),
    enabled: authed(),
    staleTime: 60_000,
  });
}

export function useAdminAuditLogStats() {
  return useQuery({
    queryKey: ['admin', 'audit-logs-stats'],
    queryFn: () => admin.getAdminAuditLogStats(),
    enabled: authed(),
    staleTime: 30_000,
  });
}


